import React, { useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import * as XLSX from "sheetjs-style";
import "./ExportButtons.css";

/**
 * Collects every row of a paged API: `fetchPage(page, limit)` returns { rows, pages }.
 * Used by reports to export all results of the current filters, not just the visible page.
 */
export async function collectPages(fetchPage, { limit = 100, maxPages = 500 } = {}) {
  const all = [];
  for (let page = 1; page <= maxPages; page++) {
    const { rows = [], pages = 1 } = (await fetchPage(page, limit)) || {};
    all.push(...rows);
    if (page >= pages || rows.length === 0) break;
  }
  return all;
}

const cellText = (v) => {
  if (v === null || v === undefined) return "";
  if (v instanceof Date) return moment(v).format("DD MMM YYYY, hh:mm A");
  return String(v);
};

const safeName = (s) => String(s || "report").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();

/**
 * "Excel" and "PDF" export buttons for a report.
 * - columns: [{ header, value: (row) => any }]
 * - getRows: async () => rows (all rows of the current filters; see collectPages)
 * - title: report name, used in the PDF heading and the file name
 * - filters: optional text describing the active filters (shown in the PDF)
 */
const ExportButtons = ({ title, columns, getRows, filters = "", disabled = false, size = "sm" }) => {
  const [busy, setBusy] = useState("");

  const load = async () => {
    const rows = await getRows();
    if (!rows?.length) {
      toast.info("Nothing to export for these filters.");
      return null;
    }
    return rows.map((r) => columns.map((c) => c.value(r)));
  };

  const file = (ext) => `${safeName(title)}-${moment().format("YYYY-MM-DD-HHmm")}.${ext}`;

  const toExcel = async () => {
    setBusy("excel");
    try {
      const body = await load();
      if (!body) return;
      const header = columns.map((c) => c.header);
      const cells = body.map((row) => row.map((v) => (typeof v === "number" && Number.isFinite(v) && Math.abs(v) < 1e10 ? v : cellText(v))));
      const sheet = XLSX.utils.aoa_to_sheet([header, ...cells]);
      // Bold header, sensible column widths, header row stays visible when scrolling
      header.forEach((_, i) => {
        const ref = XLSX.utils.encode_cell({ r: 0, c: i });
        if (sheet[ref]) {
          sheet[ref].s = {
            font: { bold: true, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "153D77" } },
            alignment: { vertical: "center" },
          };
        }
      });
      sheet["!cols"] = header.map((h, i) => ({
        wch: Math.min(50, Math.max(String(h).length, ...cells.slice(0, 500).map((row) => cellText(row[i]).length)) + 2),
      }));
      sheet["!freeze"] = { xSplit: 0, ySplit: 1 };
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, String(title || "Report").slice(0, 31));
      XLSX.writeFile(book, file("xlsx"));
      toast.success(`Exported ${body.length} row${body.length === 1 ? "" : "s"} to Excel.`);
    } catch (e) {
      toast.error(e?.message || "Could not export.");
    } finally {
      setBusy("");
    }
  };

  const toPdf = async () => {
    setBusy("pdf");
    try {
      const body = await load();
      if (!body) return;
      const [{ jsPDF }, { default: autoTable }] = await Promise.all([import("jspdf"), import("jspdf-autotable")]);
      const doc = new jsPDF({ orientation: columns.length > 5 ? "landscape" : "portrait", unit: "pt", format: "a4" });
      // The built-in PDF fonts have no rupee sign
      const pdfText = (v) => String(v).replace(/₹\s?/g, "Rs. ");
      doc.setFontSize(15);
      doc.setTextColor(21, 61, 119);
      doc.text(title || "Report", 40, 40);
      doc.setFontSize(9);
      doc.setTextColor(110);
      const meta = [`Generated ${moment().format("DD MMM YYYY, hh:mm A")}`, `${body.length} row${body.length === 1 ? "" : "s"}`, filters ? `Filters: ${filters}` : ""]
        .filter(Boolean)
        .join("   ·   ");
      doc.text(doc.splitTextToSize(pdfText(meta), doc.internal.pageSize.getWidth() - 80), 40, 56);
      autoTable(doc, {
        startY: 70,
        head: [columns.map((c) => pdfText(c.header))],
        body: body.map((row) => row.map((v) => pdfText(cellText(v)))),
        styles: { fontSize: columns.length > 9 ? 6.5 : 8, cellPadding: 4, overflow: "linebreak" },
        headStyles: { fillColor: [21, 61, 119], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [244, 247, 252] },
        margin: { left: 30, right: 30 },
        didDrawPage: () => {
          const h = doc.internal.pageSize.getHeight();
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(`Polybionic · ${title || "Report"} · Page ${doc.internal.getNumberOfPages()}`, 30, h - 16);
        },
      });
      doc.save(file("pdf"));
      toast.success(`Exported ${body.length} row${body.length === 1 ? "" : "s"} to PDF.`);
    } catch (e) {
      toast.error(e?.message || "Could not export.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="export-buttons">
      <Button size={size} variant="outline-success" onClick={toExcel} disabled={disabled || !!busy} title="Download as Excel (.xlsx)">
        {busy === "excel" ? <Spinner size="sm" /> : "Export Excel"}
      </Button>
      <Button size={size} variant="outline-danger" onClick={toPdf} disabled={disabled || !!busy} title="Download as PDF">
        {busy === "pdf" ? <Spinner size="sm" /> : "Export PDF"}
      </Button>
    </div>
  );
};

export default ExportButtons;
